/* =========================================================
   PIERGER CRO THEME — theme.js
   Módulos: header, announcement bar, menú móvil, búsqueda,
   scroll reveal, parallax, cart drawer (AJAX Cart API),
   quick add, galería PDP, variantes, countdown, sticky ATC.
   Sin dependencias externas. ES2019+, defer.
   ========================================================= */
(() => {
  'use strict';

  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const fmtMoney = (cents) => (cents / 100).toLocaleString('es-CL', { style: 'currency', currency: (window.PiergerTheme && window.PiergerTheme.currency) || 'CLP', maximumFractionDigits: 0 });

  /* ---------------------------------------------------
     1. HEADER: condensar al hacer scroll + announcement bar dismiss
     --------------------------------------------------- */
  const header = qs('[data-site-header]');
  if (header) {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-condensed', y > 24);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const announcement = qs('[data-announcement-bar]');
  const closeBtn = qs('[data-announcement-close]');
  if (announcement && closeBtn) {
    if (sessionStorage.getItem('pierger:announcement-dismissed') === '1') {
      announcement.hidden = true;
    }
    closeBtn.addEventListener('click', () => {
      announcement.hidden = true;
      sessionStorage.setItem('pierger:announcement-dismissed', '1');
    });
  }

  /* ---------------------------------------------------
     2. BÚSQUEDA (toggle)
     --------------------------------------------------- */
  const searchToggle = qs('[data-search-toggle]');
  const searchPanel = qs('[data-search-panel]');
  if (searchToggle && searchPanel) {
    searchToggle.addEventListener('click', () => {
      const willOpen = !searchPanel.classList.contains('is-open');
      searchPanel.classList.toggle('is-open', willOpen);
      if (willOpen) qs('input', searchPanel)?.focus();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') searchPanel.classList.remove('is-open');
    });
  }

  /* ---------------------------------------------------
     3. MENÚ MÓVIL
     --------------------------------------------------- */
  const navToggle = qs('[data-mobile-nav-toggle]');
  const mobileNav = qs('[data-mobile-nav]');
  if (navToggle && mobileNav) {
    navToggle.addEventListener('click', () => {
      const open = mobileNav.classList.toggle('is-open');
      navToggle.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---------------------------------------------------
     4. SCROLL REVEAL
     --------------------------------------------------- */
  if ('IntersectionObserver' in window) {
    const revealEls = qsa('.reveal, .reveal-stagger');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    revealEls.forEach((el) => io.observe(el));
  } else {
    qsa('.reveal, .reveal-stagger').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------------------------------------------------
     5. PARALLAX SUTIL (hero / imágenes marcadas)
     --------------------------------------------------- */
  const parallaxEls = qsa('.parallax-el');
  if (parallaxEls.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let ticking = false;
    const updateParallax = () => {
      const vh = window.innerHeight;
      parallaxEls.forEach((el) => {
        const rect = el.closest('[data-parallax-wrap]')?.getBoundingClientRect() || el.getBoundingClientRect();
        const progress = (rect.top) / vh; // -1..1 aprox
        const speed = parseFloat(el.dataset.parallaxSpeed || '18');
        el.style.transform = `translate3d(0, ${(-progress * speed).toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(updateParallax); ticking = true; }
    }, { passive: true });
    updateParallax();
  }

  /* ---------------------------------------------------
     6. CART DRAWER — Cart AJAX API
     --------------------------------------------------- */
  const drawer = qs('[data-cart-drawer]');
  const overlay = qs('[data-drawer-overlay]');
  const drawerBody = qs('[data-cart-drawer-body]');
  const drawerFooter = qs('[data-cart-drawer-footer]');
  const cartCountEls = qsa('[data-cart-count]');

  const openDrawer = () => {
    drawer?.classList.add('is-open');
    overlay?.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  };
  const closeDrawer = () => {
    drawer?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  };
  qsa('[data-cart-drawer-open]').forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); fetchCart(); }));
  qsa('[data-cart-drawer-close]').forEach((btn) => btn.addEventListener('click', closeDrawer));
  overlay?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  function renderCartLine(item) {
    const variantText = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-line__variant">${item.variant_title}</p>` : '';
    return `
      <div class="cart-line" data-line-key="${item.key}">
        <div class="cart-line__image">
          <img src="${item.image ? item.image.replace('.jpg', '_150x.jpg').replace('.png', '_150x.png') : ''}" alt="${item.product_title}" loading="lazy" width="76" height="76">
        </div>
        <div>
          <p class="cart-line__title">${item.product_title}</p>
          ${variantText}
          <div class="qty-selector">
            <button type="button" data-qty-decrease aria-label="Restar">−</button>
            <input type="text" value="${item.quantity}" data-qty-input inputmode="numeric" aria-label="Cantidad">
            <button type="button" data-qty-increase aria-label="Sumar">+</button>
          </div>
          <button type="button" class="cart-line__remove" data-line-remove>Eliminar</button>
        </div>
        <div class="cart-line__price">${fmtMoney(item.final_line_price)}</div>
      </div>`;
  }

  function renderDrawer(cart) {
    if (!drawerBody || !drawerFooter) return;
    if (!cart.items.length) {
      drawerBody.innerHTML = `<div class="cart-drawer__empty"><p>Tu carrito está vacío.</p><a href="/collections/all" class="btn btn--primary" data-cart-drawer-close>Ver productos</a></div>`;
      drawerFooter.innerHTML = '';
      cartCountEls.forEach((el) => { el.textContent = '0'; el.hidden = true; });
      return;
    }
    drawerBody.innerHTML = cart.items.map(renderCartLine).join('');
    drawerFooter.innerHTML = `
      <div class="cart-subtotal-row"><span>Subtotal</span><span>${fmtMoney(cart.total_price)}</span></div>
      <p style="font-size:12px;opacity:.6;margin-bottom:16px;">Envío e impuestos calculados en el checkout.</p>
      <a href="/checkout" class="btn btn--primary btn--block">Finalizar compra</a>
    `;
    cartCountEls.forEach((el) => { el.textContent = String(cart.item_count); el.hidden = cart.item_count === 0; });
    bindLineEvents();
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    renderDrawer(cart);
    return cart;
  }

  async function changeLine(key, quantity) {
    drawerBody?.classList.add('is-loading');
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const cart = await res.json();
    drawerBody?.classList.remove('is-loading');
    renderDrawer(cart);
  }

  function bindLineEvents() {
    qsa('[data-line-key]', drawerBody).forEach((line) => {
      const key = line.dataset.lineKey;
      const input = qs('[data-qty-input]', line);
      qs('[data-qty-increase]', line)?.addEventListener('click', () => changeLine(key, parseInt(input.value, 10) + 1));
      qs('[data-qty-decrease]', line)?.addEventListener('click', () => changeLine(key, Math.max(0, parseInt(input.value, 10) - 1)));
      input?.addEventListener('change', () => changeLine(key, Math.max(0, parseInt(input.value, 10) || 0)));
      qs('[data-line-remove]', line)?.addEventListener('click', () => changeLine(key, 0));
    });
  }

  async function addToCart(formData, triggerBtn) {
    triggerBtn?.classList.add('is-loading');
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'No se pudo añadir el producto.');
      }
      await fetchCart();
      openDrawer();
    } catch (err) {
      console.error('[Pierger] add to cart error:', err);
      const errorBox = triggerBtn?.closest('form')?.querySelector('[data-form-error]');
      if (errorBox) { errorBox.textContent = err.message; errorBox.classList.add('is-visible'); }
    } finally {
      triggerBtn?.classList.remove('is-loading');
    }
  }

  // Formularios de producto (PDP + quick add)
  qsa('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = qs('[data-atc-button]', form);
      addToCart(new FormData(form), btn);
    });
  });

  // Cargar contador de carrito al inicio
  fetch('/cart.js').then((r) => r.json()).then((cart) => {
    cartCountEls.forEach((el) => { el.textContent = String(cart.item_count); el.hidden = cart.item_count === 0; });
  }).catch(() => {});

  /* ---------------------------------------------------
     7. GALERÍA DE PRODUCTO (PDP)
     --------------------------------------------------- */
  qsa('[data-product-gallery]').forEach((gallery) => {
    const mainImg = qs('[data-gallery-main] img', gallery);
    qsa('[data-gallery-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        qsa('[data-gallery-thumb]', gallery).forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImg) {
          mainImg.style.opacity = 0;
          setTimeout(() => {
            mainImg.src = thumb.dataset.fullSrc;
            mainImg.style.opacity = 1;
          }, 120);
        }
      });
    });
  });

  /* ---------------------------------------------------
     8. SELECTOR DE VARIANTES
     --------------------------------------------------- */
  qsa('[data-variant-picker]').forEach((picker) => {
    let productData;
    try { productData = JSON.parse(qs('[data-product-json]', picker.closest('[data-product-root]')).textContent); } catch (e) { productData = null; }
    const idInput = qs('[data-variant-id-input]', picker.closest('form'));
    const priceEl = qs('[data-product-price]', picker.closest('[data-product-root]'));
    const atcBtn = qs('[data-atc-button]', picker.closest('form'));

    function selectedOptions() {
      return qsa('[data-option-index]', picker).map((group) => {
        const active = qs('.variant-pill.is-selected', group);
        return active ? active.dataset.value : null;
      });
    }

    function findVariant() {
      if (!productData) return null;
      const opts = selectedOptions();
      return productData.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }

    function updateUI() {
      const variant = findVariant();
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<span class="price__compare">${fmtMoney(variant.compare_at_price)}</span><span class="price__sale">${fmtMoney(variant.price)}</span>`
        : fmtMoney(variant.price);
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.textContent = variant.available ? (atcBtn.dataset.labelAvailable || 'Añadir al carrito') : (atcBtn.dataset.labelSoldOut || 'Agotado');
      }
    }

    qsa('.variant-pill', picker).forEach((pill) => {
      pill.addEventListener('click', () => {
        if (pill.disabled) return;
        const group = pill.closest('[data-option-index]');
        qsa('.variant-pill', group).forEach((p) => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');
        updateUI();
      });
    });
    updateUI();
  });

  /* ---------------------------------------------------
     9. TIMER DE URGENCIA (persistente por producto, localStorage)
     --------------------------------------------------- */
  qsa('[data-urgency-timer]').forEach((el) => {
    const minutes = parseInt(el.dataset.durationMinutes || '30', 10);
    const key = `pierger:urgency:${el.dataset.productId || 'global'}`;
    let deadline = parseInt(localStorage.getItem(key), 10);
    if (!deadline || deadline < Date.now()) {
      deadline = Date.now() + minutes * 60 * 1000;
      localStorage.setItem(key, String(deadline));
    }
    const tick = () => {
      const diff = Math.max(0, deadline - Date.now());
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      el.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      if (diff <= 0) {
        deadline = Date.now() + minutes * 60 * 1000;
        localStorage.setItem(key, String(deadline));
      }
    };
    tick();
    setInterval(tick, 1000);
  });

  /* ---------------------------------------------------
     10. STICKY ADD TO CART (móvil)
     --------------------------------------------------- */
  const stickyAtc = qs('[data-sticky-atc]');
  const mainAtcBtn = qs('[data-atc-button][data-main-atc]');
  if (stickyAtc && mainAtcBtn && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => {
      stickyAtc.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 200);
    }, { threshold: 0 });
    io.observe(mainAtcBtn);
    qs('[data-sticky-atc-trigger]', stickyAtc)?.addEventListener('click', () => {
      mainAtcBtn.closest('form')?.requestSubmit(mainAtcBtn);
    });
  }

  /* ---------------------------------------------------
     11b. STEPPER DE CANTIDAD GENÉRICO (fuera del cart drawer,
          p. ej. en la PDP)
     --------------------------------------------------- */
  qsa('.qty-selector').forEach((selector) => {
    if (selector.closest('[data-cart-drawer-body]')) return; // ya gestionado por bindLineEvents
    const input = qs('[data-qty-input]', selector);
    if (!input) return;
    qs('[data-qty-increase]', selector)?.addEventListener('click', () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    qs('[data-qty-decrease]', selector)?.addEventListener('click', () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* ---------------------------------------------------
     11. ACCORDION (mejora progresiva sobre <details>)
     --------------------------------------------------- */
  qsa('.product-accordion details').forEach((d) => {
    d.addEventListener('toggle', () => {
      if (d.open) {
        qsa('details', d.closest('.product-accordion')).forEach((other) => { if (other !== d) other.open = false; });
      }
    });
  });
})();
